import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { FormattedDate } from 'react-intl';
import type { UserMembershipDateForNodeFragment } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';

interface Props {
  /**
   * The user to display the join date for.
   */
  user: UserMembershipDateForNodeFragment;
  /**
   * Set a custom element for this component.
   */
  as?: React.ElementType;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Prepend the text "Joined" to the date.
   */
  textKey: 'noPrefix' | 'withPrefix';
}

/**
 * Displays the user's membership join date. Requires a context node of type "grouphub".
 *
 * @constructor
 *
 * @author Jonathan Bridges
 */
const UserMembershipDateForNode: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  as: Component = 'span',
  className,
  textKey = 'noPrefix'
}: Props) => {
  const cx = useClassNameMapper();
  const { formatMessageAsNode, loading: textLoading } = useTranslation(
    EndUserComponent.USER_MEMBERSHIP_DATE_FOR_NODE
  );
  const { contextNode } = useContext(AppContext);

  if (textLoading) {
    return null;
  }

  const isContextNodeGroupHub = contextNode.nodeType === NodeType.GROUPHUB;

  return (
    isContextNodeGroupHub &&
    user.joinDate && (
      <Component className={cx(className)}>
        {formatMessageAsNode(textKey, {
          date: (
            <FormattedDate
              key={`join-date-user-${user.id}`}
              value={user.joinDate}
              year="numeric"
              month="long"
              day="2-digit"
            />
          )
        })}
      </Component>
    )
  );
};

export default UserMembershipDateForNode;
